import React, { useMemo, useState } from 'react';
import {
  ProjectFeeSection,
  ProjectFeeComponent,
  FeeComponentType,
  formatCurrency,
} from '../../../../InvoiceRegister/config';

interface NumberCellProps {
  className: string;
  value: number | null;
  onValueChange: (value: number | null) => void;
  // What to emit when the field is cleared. `null` for nullable columns
  // (hours/rate); `0` for the non-nullable amount column.
  emptyValue: number | null;
  step?: string;
  min?: string;
  disabled?: boolean;
  title?: string;
}

/**
 * Numeric input that keeps its own text buffer while focused so the user can
 * type freely — clearing the field and typing decimals like "0.5" — without a
 * canonical `0` being re-rendered into the box (the "stuck leading zero" bug).
 * When unfocused it shows the canonical value, with 0/null rendered as empty.
 */
const NumberCell: React.FC<NumberCellProps> = ({
  className,
  value,
  onValueChange,
  emptyValue,
  step,
  min,
  disabled,
  title,
}) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');

  const canonical = value == null || value === 0 ? '' : String(value);

  return (
    <input
      type="number"
      className={className}
      value={focused ? draft : canonical}
      onFocus={() => {
        setDraft(canonical);
        setFocused(true);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        if (raw === '') {
          onValueChange(emptyValue);
          return;
        }
        const parsed = parseFloat(raw);
        if (!Number.isNaN(parsed)) onValueChange(parsed);
      }}
      onBlur={() => setFocused(false)}
      step={step}
      min={min}
      disabled={disabled}
      title={title}
    />
  );
};

interface SectionEditorProps {
  section: ProjectFeeSection;
  onSectionChange: (updates: Partial<ProjectFeeSection>) => void;
  // Probable is separate from onSectionChange because it is the one field a
  // LOCKED section can still change, and the autosave skips locked sections
  // entirely — the parent routes it through its own RPC. See FeesTab's
  // handleProbableChange.
  onProbableChange: (next: boolean) => void;
  onAddComponent: (type: FeeComponentType, defaults?: Partial<ProjectFeeComponent>) => void;
  onUpdateComponent: (componentIndex: number, updates: Partial<ProjectFeeComponent>) => void;
  onRemoveComponent: (componentIndex: number) => void;
  onRemoveSection: () => void;
  // Locked = read-only because the section has progress claims against it.
  // Inputs disabled; remove buttons hidden; "X claims" badge shown.
  locked?: boolean;
  // Whether the section itself can be removed (the × in its header).
  // Independent of `locked` so existing-but-unclaimed sections in amend mode
  // can still be edited yet not deleted.
  canRemoveSection?: boolean;
}

const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  onSectionChange,
  onProbableChange,
  onAddComponent,
  onUpdateComponent,
  onRemoveComponent,
  onRemoveSection,
  locked = false,
  canRemoveSection = true,
}) => {
  const sectionTotal = useMemo(
    () => section.components.reduce((s, c) => s + (c.amount || 0), 0),
    [section.components]
  );

  const isCustom = section.phase === 'Custom';
  const isItemBased = !!section.is_item_based;
  const isProbable = !!section.is_probable;
  // Billed for VALUE (not merely listed at 0%). Such a phase has by
  // definition been awarded, and set_fee_section_probable refuses to mark it
  // probable — so the tick is disabled rather than offered and then rejected.
  // Un-ticking stays available, because that is the way out of a bad state.
  const billedForValue = Number(section.claimed_to_date ?? 0) > 0;
  // The Probable PHASE is a placeholder for a whole unawarded job, so it can
  // only ever be probable — the DB CHECK refuses anything else. Its toggle is
  // shown (so the section reads the same as any other) but locked. Winning
  // the job means deleting this section and pricing the real phases, not
  // unticking a box.
  const isProbablePhase = section.phase === 'Probable';
  // Read-only readout from get_project_fee_for_entity. Measured against the
  // SAVED fee_total the claim was made on, not the live sectionTotal, so it
  // doesn't wobble while components are being typed. Deliberately not
  // claimed_to_date: that is a sum of cumulative claims, fit only for `> 0`.
  const savedFeeTotal = Number(section.fee_total) || 0;
  const invoicedPct =
    Number(section.invoiced_to_date ?? 0) > 0 && savedFeeTotal > 0
      ? Math.round((Number(section.invoiced_to_date) / savedFeeTotal) * 100)
      : null;
  const probableLocked = isProbablePhase || (billedForValue && !isProbable);
  // Claim type can only be changed while the section is empty (and unlocked):
  // switching mode after components exist would orphan them.
  const typeLocked = locked || section.components.length > 0;

  return (
    <div className={`fee-section${isCustom ? ' fee-section--custom' : ''}${locked ? ' fee-section--locked' : ''}${isProbable ? ' fee-section--probable' : ''}`}>
      <div className="fee-section__header">
        <div className="fee-section__heading">
          {isCustom ? (
            <input
              type="text"
              className="fee-section__custom-heading-input"
              value={section.name ?? ''}
              onChange={(e) => onSectionChange({ name: e.target.value })}
              placeholder="Phase name (e.g. Site Inspections)"
              disabled={locked}
            />
          ) : (
            <span className="fee-section__stage">{section.phase}</span>
          )}
          {locked && (
            <span
              className="fee-section__locked-badge"
              title="This section has been used on a progress claim and is locked. Add a new variation section for additional work."
            >
              Billed · locked
            </span>
          )}
        </div>
        <div className="fee-section__header-right">
          {/* Deliberately NOT disabled by `locked`. Probable says whether the
              scope is awarded, not what it costs, so an unticked-by-mistake
              billed section must still be correctable — see
              set_fee_section_probable, which skips the claims guard. The one
              thing it will not allow is TICKING a phase already billed for
              value, which is what probableLocked mirrors. */}
          <label
            className={`fee-section__probable${isProbable ? ' is-on' : ''}${probableLocked ? ' is-disabled' : ''}`}
            title={
              isProbablePhase
                ? 'A Probable section is always probable. Once the job is won, delete it and add the real phases.'
                : probableLocked
                  ? 'Already billed, so this phase has been awarded and can no longer be marked probable.'
                  : "Quoted but not yet awarded. Shown in the Budget page's Probable column and kept out of Unallocated until it's won."
            }
          >
            <input
              type="checkbox"
              checked={isProbable}
              disabled={probableLocked}
              onChange={(e) => onProbableChange(e.target.checked)}
            />
            <span>Probable</span>
          </label>
          {invoicedPct != null && (
            <span
              className="fee-section__invoiced"
              title={`${formatCurrency(Number(section.invoiced_to_date))} invoiced to date (latest claim, drafts included)`}
            >
              {invoicedPct}% invoiced
            </span>
          )}
          <div className="fee-section__total">{formatCurrency(sectionTotal)}</div>
          {canRemoveSection && !locked && (
            <button
              className="fee-section__remove"
              onClick={onRemoveSection}
              title="Remove this phase"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {isProbablePhase && (
        <p className="fee-section__probable-hint">
          A placeholder for a job that isn't won yet — enter the likely total
          below. It shows on the Budget page as Probable and never counts
          toward Won Fee. Once the job is won, delete this section and price
          the real phases.
        </p>
      )}

      {/* The optional notes/sub-heading is editable normally, but on a locked
          section it only shows when it actually carries a value — an empty
          disabled placeholder is just noise. */}
      {!isCustom && (!locked || !!section.name?.trim()) && (
        <div className="fee-section__name-row">
          <input
            type="text"
            className="fee-section__name-input"
            value={section.name ?? ''}
            onChange={(e) => onSectionChange({ name: e.target.value })}
            placeholder="Optional notes / sub-heading"
            disabled={locked}
          />
        </div>
      )}

      <div className="fee-section__components">
        <div className="fee-section__components-header">
          <span className="fc-col fc-col--type">Type</span>
          <span className="fc-col fc-col--desc">Description</span>
          <span className="fc-col fc-col--member">Role</span>
          <span className="fc-col fc-col--hours">Qty</span>
          <span className="fc-col fc-col--rate">Rate</span>
          <span className="fc-col fc-col--amount">Amount</span>
          <span className="fc-col fc-col--actions"></span>
        </div>

        {section.components.map((c, ci) => (
          <div key={c.id ?? `new-${ci}`} className={`fee-section__component fee-section__component--${c.component_type}`}>
            {isItemBased ? (
              // Item sections hold a single fixed 'item' component — no type choice.
              <span className="fc-input fc-input--type fc-input--readonly">Items</span>
            ) : (
              <select
                className="fc-input fc-input--type"
                value={c.component_type}
                onChange={(e) => {
                  const newType = e.target.value as FeeComponentType;
                  // 'time' bills as hours × rate and carries a role; 'allowance' is a flat amount.
                  const isTime = newType === 'time';
                  onUpdateComponent(ci, {
                    component_type: newType,
                    hours: isTime ? (c.hours ?? 0) : null,
                    rate: isTime ? (c.rate ?? 0) : c.rate,
                    team_member_type: isTime ? c.team_member_type : null,
                  });
                }}
                disabled={locked}
              >
                <option value="time">Time</option>
                <option value="allowance">Lump sum</option>
              </select>
            )}

            <input
              type="text"
              className="fc-input fc-input--desc"
              value={c.description}
              onChange={(e) => onUpdateComponent(ci, { description: e.target.value })}
              placeholder={c.component_type === 'time' ? 'e.g. Engineering' : c.component_type === 'item' ? 'e.g. Site Visit' : 'Description'}
              disabled={locked}
            />

            {c.component_type === 'time' ? (
              <select
                className="fc-input fc-input--member"
                value={c.team_member_type ?? ''}
                onChange={(e) => onUpdateComponent(ci, { team_member_type: e.target.value || null })}
                disabled={locked}
              >
                <option value="">—</option>
                <option value="Engineer">Engineer</option>
                <option value="Draftsperson">Draftsperson</option>
                <option value="Accounts">Accounts</option>
              </select>
            ) : (
              <span className="fc-input fc-input--member fc-input--readonly">—</span>
            )}

            {c.component_type === 'time' || c.component_type === 'item' ? (
              <NumberCell
                className="fc-input fc-input--hours"
                value={c.hours}
                onValueChange={(v) => onUpdateComponent(ci, { hours: v })}
                emptyValue={null}
                step={c.component_type === 'item' ? '1' : '0.5'}
                min="0"
                disabled={locked}
              />
            ) : (
              <span className="fc-input fc-input--hours fc-input--readonly">—</span>
            )}

            {c.component_type === 'time' || c.component_type === 'item' ? (
              <NumberCell
                className="fc-input fc-input--rate"
                value={c.rate}
                onValueChange={(v) => onUpdateComponent(ci, { rate: v })}
                emptyValue={null}
                step="1"
                min="0"
                disabled={locked}
              />
            ) : (
              <span className="fc-input fc-input--rate fc-input--readonly">—</span>
            )}

            <NumberCell
              className="fc-input fc-input--amount"
              value={c.amount}
              onValueChange={(v) => onUpdateComponent(ci, { amount: v ?? 0 })}
              emptyValue={0}
              step="0.01"
              min="0"
              disabled={locked || c.component_type === 'time' || c.component_type === 'item'}
              title={c.component_type === 'time' || c.component_type === 'item' ? 'Auto-calculated from qty × rate' : undefined}
            />

            {!locked && (
              <button
                className="fc-remove-btn"
                onClick={() => onRemoveComponent(ci)}
                title="Remove component"
              >
                ×
              </button>
            )}
          </div>
        ))}

        {!locked && (
          <div className="fee-section__add-buttons">
            {isItemBased ? (
              // Item sections hold exactly one item component (count × rate).
              <button
                className="fee-section__add-btn"
                onClick={() => onAddComponent('item')}
                disabled={section.components.length >= 1}
                title={section.components.length >= 1
                  ? 'An item phase has a single item — change its description/qty/rate above'
                  : undefined}
              >
                + Items
              </button>
            ) : (
              <>
                <button
                  className="fee-section__add-btn"
                  onClick={() => onAddComponent('time', { team_member_type: 'Engineer', description: 'Engineering' })}
                >
                  + Engineer time
                </button>
                <button
                  className="fee-section__add-btn"
                  onClick={() => onAddComponent('time', { team_member_type: 'Draftsperson', description: 'Drafting' })}
                >
                  + Draftsperson time
                </button>
                <button
                  className="fee-section__add-btn"
                  onClick={() => onAddComponent('allowance')}
                >
                  + Lump sum
                </button>
              </>
            )}
            <select
              className="fee-section__claim-type"
              value={isItemBased ? 'item' : 'percent'}
              onChange={(e) => onSectionChange({ is_item_based: e.target.value === 'item' })}
              disabled={typeLocked}
              title={typeLocked
                ? 'Claim type locks once the section has components — remove them to change it'
                : 'How this phase is billed: by % complete, or by item count'}
            >
              <option value="percent">% Complete</option>
              <option value="item">Items</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionEditor;
